import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './LeaveReview.module.scss';

// Use env API base (fallback to local /api if missing)
const API = process.env.REACT_APP_API_URL || '/api';

// Minimal typings for Turnstile on window
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: any) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const LeaveReview: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();

  const [name, setName] = useState('');
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [contact, setContact] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Render Turnstile widget (uses test or real site key from .env)
  useEffect(() => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      // Not configured in dev -> backend middleware allows request anyway
      console.warn('Turnstile site key missing; widget not rendered (dev mode).');
      return;
    }

    const tryRender = () => {
      if (!widgetContainerRef.current) return;
      if (!window.turnstile) return; // script not loaded yet
      if (widgetIdRef.current) return; // already rendered

      widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => setTurnstileToken(token),
        'response-field': false,
        theme: 'auto',
        size: 'flexible',
      });
    };

    tryRender();
    // Poll briefly until script is ready
    const id = setInterval(tryRender, 200);
    const kill = setTimeout(() => clearInterval(id), 5000);
    return () => {
      clearInterval(id);
      clearTimeout(kill);
    };
  }, []);

  const getBusinessOwnerUid = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/owner`);
      if (!res.ok) throw new Error('Failed to get business owner');
      const data = await res.json();
      return data.firebaseUid as string;
    } catch (err) {
      console.error('Error fetching owner UID:', err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!comment || rating < 1) {
      setError('Merci de donner une note et un commentaire.');
      return;
    }

    const firebaseUid = await getBusinessOwnerUid();
    if (!firebaseUid) {
      setError("Impossible de trouver l'identifiant du propriétaire.");
      return;
    }

    try {
      const res = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId,
          author: name.trim(),
          rating,
          content: comment,
          contact: contact.trim(),
          firebaseUid,
          // send Turnstile token (server middleware will verify if TURNSTILE_SECRET_KEY is set)
          turnstileToken: turnstileToken || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Erreur lors de l'envoi. Veuillez réessayer.");
      }

      // Reset form
      setName('');
      setRating(0);
      setComment('');
      setContact('');
      setSuccess(true);

      // Reset widget so next submission gets a fresh token
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      setTurnstileToken('');
    } catch (err: any) {
      setError(err.message || 'Erreur réseau.');
    }
  };

  if (success) {
    return (
      <div className={styles.leaveReviewContainer}>
        <h2>Merci pour votre avis !</h2>
        <p>Votre retour a bien été enregistré.</p>
      </div>
    );
  }

  return (
    <div className={styles.leaveReviewContainer}>
      <h2>Laisser un avis</h2>
      <form onSubmit={handleSubmit} className={styles.leaveReviewForm}>
        <label>
          Nom
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={32} />
        </label>

        <label>
          Email ou téléphone (pour recevoir une réponse)
          <input
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            maxLength={64}
            placeholder="ex: email@exemple.com ou +33612345678"
          />
        </label>

        <label>
          Note
          <div className={styles.ratingRow}>
            {[1, 2, 3, 4, 5].map((n) => (
              <span
                key={n}
                className={n <= rating ? styles.starActive : styles.star}
                onClick={() => setRating(n)}
                role="button"
                tabIndex={0}
                aria-label={`Note ${n}`}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setRating(n)}
              >
                ★
              </span>
            ))}
          </div>
        </label>

        <label>
          Commentaire <span style={{ color: 'red' }}>*</span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            rows={4}
            maxLength={500}
          />
        </label>

        {/* Turnstile widget renders here (if site key provided) */}
        <div style={{ margin: '12px 0' }} ref={widgetContainerRef} />

        {error && <div className={styles.errorMsg}>{error}</div>}

        <button type="submit" className={styles.submitBtn}>Envoyer</button>
      </form>
    </div>
  );
};

export default LeaveReview;
